import React, { useState, useEffect } from 'react';
import { Container } from 'reactstrap';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../../context';
import {
    PracticeIcon,
    FileStorageIcon,
    StagingIcon,
    IntermediateIcon,
    LeadsGenerationIcon,
    MissionControlIcon,
} from './StatusIcons';
import '../../../../Overview.css';
import '../OverviewStatus.css';
import OverviewSyncStatusCard from './OverviewSyncStatusCard';
import SyncConfirmationModal from '../../../../../Modals/SyncConfirmationModal';
import hasPermission from '../../../../../Shared/hasPermission';
import InstanceReportCard from './Cards/InstanceReport';
import { getSyncCardText } from './SyncTime';
import { allPropsHaveValue } from '../../../../../Shared/PropHelper';
import {
    Heartbeat,
    Sync,
    StagingLoad,
    IntermediateLoad,
    LeadsGeneration,
} from '../SyncConstants';
import {
    getHeartbeatStyle,
    getExtractionStyle,
    getLoaderStyle,
} from '../../../../../Shared/SyncStyles';
import DaylightSyncCard from './DaylightSyncCard';
import AttachmentSyncModal from '../../../../../Modals/AttachmentSyncModal';

// TODO: talk over the best way to break this file up
// TODO: compare these class functions with the status class component I just put in shared
// TODO: and compare with the status helper file in shared
const SyncStatus = () => {
    const dispatch = useDispatch();

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const can = (permission) => hasPermission(permission, permissions);
    const canRestartAgent = can('ManageAgentRestart');
    const canManageDaylightSync = can('ManageDaylightSync');
    const canDaylightReview = can('DaylightReview');
    const canDaylightFlag = can('DaylightFlagForReview');
    const canExtract = hasPermission('ManageExtraction', permissions);
    const canStaging = hasPermission('ManageStagingLoad', permissions);
    const canIntermed = hasPermission('ManageIntermediateLoad', permissions);
    const canManageReportingSync = hasPermission(
        'ManageReportingSync',
        permissions
    );
    const canManageLeadsGeneration = hasPermission(
        'ManageLeadsGeneration',
        permissions
    );
    const canManageConversions = hasPermission(
        'ManageConversions',
        permissions
    );
    const canViewDaylightCodeMapping = canDaylightFlag || canDaylightReview;
    const canSyncConfirmation =
        canRestartAgent ||
        canExtract ||
        canStaging ||
        canIntermed ||
        canManageLeadsGeneration ||
        canManageDaylightSync ||
        canManageConversions;

    const {
        runtimeExtraction,
        runtimeStaging,
        runtimeIntermediate,
        practiceId,
        practiceName,
        hasHadHeartbeat,
        hasHeartbeatError,
        lastHeartbeatTime,
        hasHadExtraction,
        extractionError,
        hasScheduleIssue,
        extractionActive,
        lastExtractionRun,
        lastStagingLoadRun,
        lastIntermediateLoadRun,
        hasHadStagingLoad,
        stagingLoadError,
        stagingLoadActive,
        hasHadIntermediateLoad,
        intermediateLoadError,
        intermediateLoadActive,
        isDaylightConnected,
        practiceOfferings,
        allowConversionPrep,
        hasHadConversionPrep,
        conversionPrepError,
        conversionPrepActive,
        lastConversionPrepRun,
        latestConversionRuntime,
        isCloudPims,
        hasHadLeadsGeneration,
        leadsGenerationError,
        hasLeadsGenerationIssue,
        leadsGenerationActive,
        lastLeadsGenerationRun,
        latestLeadsGenerationRuntime,
        isMissionControlAlive,
        missionControlLastHeartbeat,
    } = useSelector(
        (state) => ({
            runtimeExtraction: state.practice.latestRuntimeExtraction,
            runtimeStaging: state.practice.latestRuntimeStaging,
            runtimeIntermediate: state.practice.latestRuntimeIntermediate,
            practiceId: state.practice.practice?.id,
            practiceName: state.practice.practice?.name,
            hasHadHeartbeat: state.practice.practice?.hasHadHeartbeat,
            hasHeartbeatError: state.practice.practice?.hasHeartbeatError,
            lastHeartbeatTime:
                state.practice.practice?.practiceStatus?.lastHeartbeatTime,
            hasHadExtraction: state.practice.practice?.hasHadExtraction,
            extractionError: state.practice.practice?.extractionError,
            hasScheduleIssue: state.practice.practice?.hasScheduleIssue,
            extractionActive: state.practice.practice?.extractionActive,
            lastExtractionRun:
                state.practice.practice?.practiceStatus?.lastExtractionRun,
            lastStagingLoadRun:
                state.practice.practice?.practiceStatus?.lastStagingLoadRun,
            lastIntermediateLoadRun:
                state.practice.practice?.practiceStatus
                    ?.lastIntermediateLoadRun,
            hasHadStagingLoad: state.practice.practice?.hasHadStagingLoad,
            stagingLoadError: state.practice.practice?.stagingLoadError,
            stagingLoadActive: state.practice.practice?.stagingLoadActive,
            hasHadIntermediateLoad:
                state.practice.practice?.hasHadIntermediateLoad,
            intermediateLoadError:
                state.practice.practice?.intermediateLoadError,
            intermediateLoadActive:
                state.practice.practice?.intermediateLoadActive,
            isDaylightConnected: state.practice.practice?.isDaylightConnected,
            practiceOfferings: state.practice.practice?.practiceOfferings,
            allowConversionPrep: state.practice.practice?.allowConversionPrep,
            hasHadConversionPrep: state.practice.practice?.hasHadConversionPrep,
            conversionPrepError: state.practice.practice?.conversionPrepError,
            conversionPrepActive: state.practice.practice?.conversionPrepActive,
            lastConversionPrepRun:
                state.practice.practice?.practiceStatus?.lastConversionPrepRun,
            latestConversionRuntime: state.practice.latestRuntimeConversionPrep,
            isCloudPims: state.practice.practice?.isCloudPims,
            hasHadLeadsGeneration:
                state.practice.practice?.practiceStatus?.hasHadLeadsGeneration,
            leadsGenerationError:
                state.practice.practice?.practiceStatus?.leadsGenerationError,
            hasLeadsGenerationIssue:
                state.practice.practice?.practiceStatus
                    ?.hasLeadsGenerationIssue,
            leadsGenerationActive:
                state.practice.practice?.practiceStatus?.leadsGenerationActive,
            lastLeadsGenerationRun:
                state.practice.practice?.practiceStatus?.lastLeadsGenerationRun,
            latestLeadsGenerationRuntime:
                state.practice.latestRuntimeLeadsGeneration,
            isMissionControlAlive:
                state.practice.practice?.practiceStatus?.isMissionControlAlive,
            missionControlLastHeartbeat:
                state.practice.practice?.practiceStatus
                    ?.missionControlLastHeartbeat,
        }),
        shallowEqual
    );

    const [syncType, setSyncType] = useState('');
    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [attachmentSyncModalOpen, setAttachmentSyncModalOpen] = useState(false);
    
    useEffect(() => {
        if (practiceId) {
            getLatestTimeExtraction(dispatch, practiceId, isCloudPims);
            getLatestTimeStagingLoad(dispatch, practiceId);
            getLatestTimeIntermediateLoad(dispatch, practiceId);
            getLatestTimeConversionPrep(dispatch, practiceId);
            getLatestTimeLeadsGeneration(dispatch, practiceId);
        }
    }, [dispatch, practiceId]);

    let cards = [];

    // Connectivity
    if (allPropsHaveValue({ hasHadHeartbeat, lastHeartbeatTime })) {
        const dropdownItems =
            canRestartAgent && !isCloudPims
                ? [
                      {
                          itemAction: () =>
                              triggerAgentRestart(
                                  setSyncType,
                                  setSyncModalOpen
                              ),
                          itemText: 'Restart Agent',
                      },
                  ]
                : [];
        const content = (
            <OverviewSyncStatusCard
                key={Heartbeat}
                classes={
                    'bg-' +
                    getHeartbeatStyle(hasHadHeartbeat, hasHeartbeatError)
                }
                icon={<PracticeIcon size={size} fill="#fff" />}
                heading="Connectivity"
                text={getSyncCardText(
                    hasHadHeartbeat,
                    lastHeartbeatTime,
                    null,
                    false
                )}
                dropdownItems={dropdownItems}
                hasPermission={canRestartAgent}
            />
        );

        cards.push(content);
    }

    // Mission Control
    if (
        allPropsHaveValue({
            isMissionControlAlive,
            missionControlLastHeartbeat,
        })
    ) {
        const hasEverHeartbeat = !(
            missionControlLastHeartbeat === null ||
            missionControlLastHeartbeat === undefined
        );
        const content = (
            <OverviewSyncStatusCard
                key="missionControl"
                classes={
                    'bg-' +
                    getHeartbeatStyle(hasEverHeartbeat, !isMissionControlAlive)
                }
                icon={<MissionControlIcon size={size} fill="#fff" />}
                heading="Mission Control"
                text={getSyncCardText(
                    hasEverHeartbeat,
                    missionControlLastHeartbeat,
                    null,
                    false
                )}
            />
        );

        cards.push(content);
    }
    // Sync
    if (
        allPropsHaveValue({
            hasHadExtraction,
            runtimeExtraction,
            lastExtractionRun,
            extractionActive,
        })
    ) {
        const dropdownItems = canExtract
            ? [
                  {
                      itemAction: () =>
                          triggerExtraction(setSyncType, setSyncModalOpen),
                      itemText: 'Sync Data',
                  },
                  {
                      itemAction: () =>
                          terminateExtraction(setSyncType, setSyncModalOpen),
                      itemText: 'Terminate Data Sync',
                  },
                  {
                      itemAction: () =>
                          triggerAttachmentSync(setSyncType, setAttachmentSyncModalOpen),
                      itemText: 'Sync Attachments',
                  },
                  {
                      itemAction: () =>
                          terminateAttachmentSync(setSyncType, setSyncModalOpen),
                      itemText: 'Terminate Attachment Sync',
                  }
              ]
            : [];
        const content = (
            <OverviewSyncStatusCard
                key={Sync}
                classes={`bg-${getExtractionStyle(
                    hasHadExtraction,
                    extractionError,
                    hasScheduleIssue,
                    extractionActive
                )}`}
                icon={<FileStorageIcon size={size} fill="#fff" />}
                heading="Sync"
                text={getSyncCardText(
                    hasHadExtraction,
                    lastExtractionRun,
                    runtimeExtraction,
                    extractionActive
                )}
                dropdownItems={dropdownItems}
                hasPermission={canExtract}
                showStaleBorder={extractionActive && extractionError}
            />
        );

        cards.push(content);
    }

    // Staging
    if (
        allPropsHaveValue({
            hasHadStagingLoad,
            runtimeStaging,
            lastStagingLoadRun,
            stagingLoadActive,
        }) &&
        !isCloudPims
    ) {
        const dropdownItems = canStaging
            ? [
                  {
                      itemAction: () =>
                          triggerStagingLoad(setSyncType, setSyncModalOpen),
                      itemText: 'Load Staging',
                  },
              ]
            : [];
        const content = (
            <OverviewSyncStatusCard
                key={StagingLoad}
                classes={`bg-${getLoaderStyle(
                    hasHadStagingLoad,
                    stagingLoadError,
                    stagingLoadActive
                )}`}
                icon={<StagingIcon size={size} fill="#fff" />}
                heading="Staging"
                text={getSyncCardText(
                    hasHadStagingLoad,
                    lastStagingLoadRun,
                    runtimeStaging,
                    stagingLoadActive
                )}
                dropdownItems={dropdownItems}
                hasPermission={canStaging}
                showStaleBorder={stagingLoadActive && stagingLoadError}
            />
        );

        cards.push(content);
    }

    // Intermediate
    if (
        allPropsHaveValue({
            hasHadIntermediateLoad,
            runtimeIntermediate,
            intermediateLoadActive,
        })
    ) {
        const dropdownItems = canIntermed
            ? [
                  {
                      itemAction: () =>
                          triggerIntermediateLoad(
                              setSyncType,
                              setSyncModalOpen
                          ),
                      itemText: 'Load Intermediate',
                  },
              ]
            : [];
        const content = (
            <OverviewSyncStatusCard
                key={IntermediateLoad}
                classes={`bg-${getLoaderStyle(
                    hasHadIntermediateLoad,
                    intermediateLoadError,
                    intermediateLoadActive
                )}`}
                icon={<IntermediateIcon size={size} fill="#fff" />}
                heading="Intermediate"
                text={getSyncCardText(
                    hasHadIntermediateLoad,
                    lastIntermediateLoadRun,
                    runtimeIntermediate,
                    intermediateLoadActive
                )}
                dropdownItems={dropdownItems}
                hasPermission={canIntermed}
                showStaleBorder={
                    intermediateLoadActive && intermediateLoadError
                }
            />
        );

        cards.push(content);
    }

    // Conversion Prep (read-only status tile — original behaviour unchanged)
    if (
        allowConversionPrep &&
        allPropsHaveValue({
            hasHadConversionPrep,
            conversionPrepError,
            conversionPrepActive,
        })
    ) {
        const content = (
            <OverviewSyncStatusCard
                classes={`bg-${getLoaderStyle(
                    hasHadConversionPrep,
                    conversionPrepError,
                    conversionPrepActive
                )}`}
                icon={<FileStorageIcon size={size} fill="#fff" />}
                heading="ConversionPrep"
                text={getSyncCardText(
                    hasHadConversionPrep,
                    lastConversionPrepRun,
                    latestConversionRuntime,
                    conversionPrepActive
                )}
            />
        );

        cards.push(content);
    }

    // Attachment Prep (new tile — action menu for internal users with ManageConversions)
    if (allowConversionPrep && canManageConversions) {
        const conversionPrepDropdownItems =
            conversionPrepActive
                ? []
                : [
                      {
                          itemAction: () =>
                              triggerAttachmentPrep(
                                  setSyncType,
                                  setSyncModalOpen
                              ),
                          itemText: 'Run Attachment Prep',
                      },
                  ];
        const content = (
            <OverviewSyncStatusCard
                key="attachmentPrep"
                classes={`bg-info`}
                icon={<FileStorageIcon size={size} fill="#fff" />}
                heading="Attachment Prep"
                text="Start Attachment Prep job"
                dropdownItems={conversionPrepDropdownItems}
                hasPermission={canManageConversions}
            />
        );

        cards.push(content);
    }

    // Daylight
    if (isDaylightConnected) {
        const triggerDaylightSync = () => {
            setSyncType('Daylight Sync');
            setSyncModalOpen(true);
        };

        const triggerCodeConverter = () => {
            setSyncType('Daylight Code Conversion');
            setSyncModalOpen(true);
        };

        const triggerCodeMapping = () => {
            dispatch(
                actions.daylight.setDaylightCodeMapperPractice(
                    practiceId,
                    practiceName
                )
            );
            dispatch(actions.daylight.toggleDaylightCodeReviewModal(true));
        };

        const content = (
            <DaylightSyncCard
                key="daylight"
                canManageDaylightSync={canManageDaylightSync}
                canViewDaylightCodeMapping={canViewDaylightCodeMapping}
                triggerSyncCallback={triggerDaylightSync}
                triggerCodeConverterCallback={triggerCodeConverter}
                triggerCodeMappingCallback={triggerCodeMapping}
            />
        );

        cards.push(content);
    }

    // Reporting Sync
    if (practiceOfferings?.some((o) => o.isReportingOffering)) {
        const content = (
            <OverviewSyncStatusCard
                classes={'bg-secondary'}
                icon={<IntermediateIcon size={size} fill="#fff" />}
                heading="Reporting Sync"
                text="Status Not Found"
                dropdownItems={[
                    {
                        itemAction: () =>
                            triggerReportingSync(setSyncType, setSyncModalOpen),
                        itemText: 'Sync Reporting',
                    },
                ]}
                hasPermission={canManageReportingSync}
            />
        );

        cards.push(content);
    }

    // Leads Generation
    // It is intentional that VRS users should not be able to see the Leads Generation tile (i.e. only visible to accounts with the BitwerxAdmin role)
    if (
        practiceOfferings?.some(
            (o) => o.isLeadsOffering && canManageLeadsGeneration
        )
    ) {
        const content = (
            <OverviewSyncStatusCard
                key={LeadsGeneration}
                classes={`bg-${getExtractionStyle(
                    hasHadLeadsGeneration,
                    leadsGenerationError,
                    hasLeadsGenerationIssue,
                    leadsGenerationActive
                )}`}
                icon={<LeadsGenerationIcon size={size} fill="#fff" />}
                heading="Leads Generation"
                text={getSyncCardText(
                    hasHadLeadsGeneration,
                    lastLeadsGenerationRun,
                    latestLeadsGenerationRuntime,
                    leadsGenerationActive
                )}
                dropdownItems={[
                    {
                        itemAction: () =>
                            dispatch(
                                actions.toggle.toggleLeadsGenerationConfirmationModal(
                                    true
                                )
                            ),
                        itemText: 'Start Leads Generation Job',
                    },
                ]}
                hasPermission={canManageLeadsGeneration}
            />
        );
        cards.push(content);
    }

    return (
        <Container className="d-flex justify-content-around flex-wrap pb-2 px-0 text-14 text-light">
            {canSyncConfirmation && (
                // TODO: refactor this modal
                <SyncConfirmationModal
                    practiceId={practiceId}
                    type={syncType}
                    isOpen={syncModalOpen}
                    closeModal={() => setSyncModalOpen(false)}
                />
            )}
            {canSyncConfirmation && (
                <AttachmentSyncModal
                    practiceId={practiceId}
                    type={syncType}
                    isOpen={attachmentSyncModalOpen}
                    closeModal={() => setAttachmentSyncModalOpen(false)}
                /> 
            )}                
            {cards}
            <InstanceReportCard />
        </Container>
    );
};

// Size of the sync card Icons
const size = '50px';

// Get the latest runtimes for practice related jobs
const getLatestTimeExtraction = async (dispatch, practiceId, isCloudPims) => {
    return dispatch(
        await actions.practice.getLatestRuntime(
            practiceId,
            isCloudPims ? 'Extraction' : 'ExtractionInitiate'
        )
    );
};

const getLatestTimeStagingLoad = async (dispatch, practiceId) => {
    return dispatch(
        actions.practice.getLatestRuntime(practiceId, 'StagingLoad')
    );
};

const getLatestTimeIntermediateLoad = async (dispatch, practiceId) => {
    return dispatch(
        actions.practice.getLatestRuntime(practiceId, 'IntermediateLoad')
    );
};

const getLatestTimeConversionPrep = async (dispatch, practiceId) => {
    return dispatch(
        actions.practice.getLatestRuntime(practiceId, 'ConversionPrep')
    );
};

const getLatestTimeLeadsGeneration = async (dispatch, practiceId) => {
    return dispatch(
        actions.practice.getLatestRuntime(practiceId, 'LeadsGeneration')
    );
};

// Sync card dropdown item actions
const triggerAgentRestart = (setSyncType, setSyncModalOpen) => {
    setSyncType('Agent Restart');
    setSyncModalOpen(true);
};

const triggerExtraction = (setSyncType, setSyncModalOpen) => {
    setSyncType('Start Data Extraction');
    setSyncModalOpen(true);
};

const terminateExtraction = (setSyncType, setSyncModalOpen) => {
    setSyncType('Terminate Data Extraction');
    setSyncModalOpen(true);
};

const triggerAttachmentSync = (setSyncType, setAttachmentSyncModalOpen) => {
    setSyncType('Attachment Sync');
    setAttachmentSyncModalOpen(true);
};

const terminateAttachmentSync = (setSyncType, setSyncModalOpen) => {
    setSyncType('Terminate Attachment Sync');
    setSyncModalOpen(true);
};

const triggerStagingLoad = (setSyncType, setSyncModalOpen) => {
    setSyncType('Staging Load');
    setSyncModalOpen(true);
};

const triggerIntermediateLoad = (setSyncType, setSyncModalOpen) => {
    setSyncType('Intermediate Load');
    setSyncModalOpen(true);
};

const triggerReportingSync = (setSyncType, setSyncModalOpen) => {
    setSyncType('Reporting Sync');
    setSyncModalOpen(true);
};

const triggerAttachmentPrep = (setSyncType, setSyncModalOpen) => {
    setSyncType('Start Attachment Prep');
    setSyncModalOpen(true);
};

const triggerLeadsGenerationJob = (setSyncType, setSyncModalOpen) => {
    setSyncType('Leads Generation');
    setSyncModalOpen(true);
};

export default SyncStatus;
